/**
 * リリース記述子（ARCHITECTURE.md §8.1）。
 *
 * 設計上の要点:
 *   - イメージは **必ずダイジェスト固定**（`@sha256:`）。タグ参照は禁止で、
 *     `:latest` はもちろん `:2026.08.1` も不可。同じリリースが時期によって
 *     別のバイナリになる余地を残さない。
 *   - 記述子は **CP が Ed25519 で署名した blob** で、エージェントは中身を
 *     組み立てず署名済み blob をそのまま `mailsv-updater` へ中継する。
 *     エージェントが侵害されても「CP が署名していないイメージ」は流れない。
 *   - `nodeId` を含めて対象ノードに束縛する。別ノード向けの記述子を
 *     使い回す攻撃を成立させない。
 */
import { z } from 'zod';

/** `ghcr.io/foo/postfix@sha256:<64hex>` 形式。タグ参照を弾く。 */
export const imageDigestRefSchema = z
  .string()
  .regex(
    /^[a-z0-9.\-_/:]+@sha256:[a-f0-9]{64}$/,
    'イメージ参照は @sha256:<64桁hex> のダイジェスト固定である必要があります',
  );

/** リリース版数。`YYYY.MM.N`。 */
export const releaseVersionSchema = z
  .string()
  .regex(/^\d{4}\.\d{2}\.\d+$/, 'リリース版数は YYYY.MM.N 形式です');

/**
 * 更新対象サービス。`order` はこの語彙で並べる。
 *
 * **`.github/workflows/release.yml` が push するイメージと 1 対 1 で対応させること。**
 * ここに無い名前は `releaseDescriptorSchema` が弾くので、リリース記述子に
 * 載せられない = **`mailsv-updater` から永久に更新できない**。
 * 実際に `netguard`（L3 の IP BAN）と `backup`（ノードのバックアップ）が
 * この状態で、イメージは毎リリース push されているのに更新経路だけが無かった。
 *
 * 名前は**リリース記述子側の語彙**であって compose の service 名ではない
 * （写像は `deploy/node/updater/mailsv-updater.sh` の `compose_service_name`）。
 * `unbound` ↔ `mailnode-resolver` のように綴りが違うものがあるので、
 * 増やすときは必ず両方を見ること。
 */
export const RELEASE_SERVICE = [
  'agent',
  'rspamd',
  'clamav',
  'dovecot',
  'postfix',
  'postsrsd',
  'unbound',
  'netguard',
  'backup',
] as const;
export type ReleaseService = (typeof RELEASE_SERVICE)[number];
export const releaseServiceSchema = z.enum(RELEASE_SERVICE);

export const releaseDescriptorSchema = z.object({
  version: releaseVersionSchema,
  /** 対象ノードに束縛する。他ノードでは検証を通さない。 */
  nodeId: z.string().min(1),
  /**
   * サービス名 → ダイジェスト固定イメージ参照。
   * キー型に enum を使うと「全サービス必須」に推論されてしまうため
   * `string` で受けて中身を検証する（部分更新のリリースを表現できるようにする）。
   */
  images: z
    .record(z.string(), imageDigestRefSchema)
    .refine(
      (m) => Object.keys(m).every((k) => (RELEASE_SERVICE as readonly string[]).includes(k)),
      { message: `images のキーは ${RELEASE_SERVICE.join(' / ')} のいずれかです` },
    ),
  /** 再作成する順序。依存の下流（rspamd）から先に入れ替える。 */
  order: z.array(releaseServiceSchema).min(1),
  /**
   * 単調バージョン制約。ノードの現在版数がこれ未満なら適用しない。
   * 「古いリリースを新しい世代で押し込んでダウングレードさせる」攻撃を防ぐ。
   */
  notInstalledBefore: releaseVersionSchema.nullable(),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});
export type ReleaseDescriptor = z.infer<typeof releaseDescriptorSchema>;

/**
 * 署名済み記述子。エージェントはこの封筒ごと updater へ渡す。
 * 署名対象は `canonicalJson(descriptor)`（`lib/canonical-json.ts`）。
 */
export const signedReleaseDescriptorSchema = z.object({
  descriptor: releaseDescriptorSchema,
  /** base64 の Ed25519 署名。 */
  signature: z.string().min(1),
  /** どの CP 署名鍵で署名したか（ローテーション中は 2 本併存する）。 */
  signingKeyId: z.string().min(1),
});
export type SignedReleaseDescriptor = z.infer<typeof signedReleaseDescriptorSchema>;

/** リリースチャネル（リング配信、§8.2）。 */
export const RELEASE_CHANNEL = ['canary', 'ring1', 'ring2'] as const;
export type ReleaseChannel = (typeof RELEASE_CHANNEL)[number];
export const releaseChannelSchema = z.enum(RELEASE_CHANNEL);

/** 適用してよい時間帯。`cron` は `tzid` のローカル時刻で解釈する。 */
export const maintenanceWindowSchema = z.object({
  /** IANA タイムゾーン（例: `Asia/Tokyo`） */
  tzid: z.string().min(1),
  cron: z.string().min(1),
  durationMin: z.number().int().positive(),
});
export type MaintenanceWindow = z.infer<typeof maintenanceWindowSchema>;

/**
 * `notInstalledBefore` の単調性チェック。
 * `YYYY.MM.N` を数値 3 つ組として比較する（文字列比較では N が 2 桁になった
 * 途端に壊れるため）。
 */
export function compareReleaseVersion(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** 現在版数が記述子の適用条件を満たすか。 */
export function isReleaseApplicable(
  descriptor: ReleaseDescriptor,
  currentVersion: string | null,
  now: Date = new Date(),
): { ok: boolean; reason?: string } {
  if (now > new Date(descriptor.expiresAt)) {
    return { ok: false, reason: 'リリース記述子の有効期限が切れています' };
  }
  if (descriptor.notInstalledBefore !== null && currentVersion !== null) {
    if (compareReleaseVersion(currentVersion, descriptor.notInstalledBefore) < 0) {
      return { ok: false, reason: `現在版数 ${currentVersion} は notInstalledBefore を満たしません` };
    }
  }
  if (currentVersion !== null && compareReleaseVersion(descriptor.version, currentVersion) <= 0) {
    return { ok: false, reason: `適用済みか、より古い版数です（現在 ${currentVersion}）` };
  }
  return { ok: true };
}
